import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Button from '../../common/Button';
import MultiSelectScrollable from '../../common/MultiSelectScrollable';
import SearchableMultiSelect from '../../common/SearchableMultiSelect';
import { validateRequired, validateEmail, validatePhone } from '../../../utils/validators';
import customerService from '../../../services/customerService';
import siteService from '../../../services/siteService';

const EditSpocModal = ({ isOpen, onClose, onSubmit, spoc }) => {
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    email: '',
    contact: '',
    // team_lead_name: '',
    company_id: '',
    site_ids: [],
    password: '',
    is_locked_out: false
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCompanies();
    }
  }, [isOpen]);

  useEffect(() => {
    if (spoc && isOpen) {
      let initialSiteIds = [];
      // Robust site ID extraction from sites objects or site_ids array
      if (Array.isArray(spoc.sites)) {
        initialSiteIds = spoc.sites.map((site) => (site.id || site.site_id)?.toString()).filter(Boolean);
      } else if (Array.isArray(spoc.site_ids)) {
        initialSiteIds = spoc.site_ids.map(id => id.toString());
      } else if (spoc.site_id) {
        initialSiteIds = [spoc.site_id.toString()];
      }

      setFormData({
        name: spoc.name || '',
        designation: spoc.designation || '',
        email: spoc.email || '',
        contact: spoc.contact || '',
        // team_lead_name: spoc.team_lead?.name || spoc.team_lead || '',
        company_id: spoc.company_id?.toString() || '',
        site_ids: initialSiteIds,
        password: '',
        is_locked_out: spoc.is_locked_out !== undefined ? spoc.is_locked_out : false
      });
      
      if (spoc.company_id) {
        loadSites(spoc.company_id);
      }
    }
  }, [spoc, isOpen]);

  useEffect(() => {
    if (formData.company_id && isOpen) {
      loadSites(formData.company_id);
    } else {
      setSites([]);
    }
  }, [formData.company_id, isOpen]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    const result = await customerService.getCustomerCompanies();
    if (result.success) setCompanies(result.data);
    setLoadingCompanies(false);
  };

  const loadSites = async (companyId) => {
    setLoadingSites(true);
    const result = await siteService.getAllSites();
    if (result.success) {
      const selectedCompanyId = parseInt(companyId, 10);
      const filteredSites = result.data.filter((site) => site.company_id === selectedCompanyId);
      setSites(filteredSites);
    }
    setLoadingSites(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      if (name === 'company_id') {
        updatedData.site_ids = [];
      }
      
      return updatedData;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSitesChange = (newSiteIds) => {
    setFormData(prev => ({ ...prev, site_ids: newSiteIds }));
    if (errors.site_ids) {
      setErrors(prev => ({ ...prev, site_ids: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!validateRequired(formData.name)) newErrors.name = 'Name is required';
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!validateRequired(formData.contact)) {
      newErrors.contact = 'Contact number is required';
    } else if (!validatePhone(formData.contact)) {
      newErrors.contact = 'Please enter a valid 10-digit phone number';
    }
    // if (!validateRequired(formData.team_lead_name)) newErrors.team_lead_name = 'Team Lead is required';
    if (!validateRequired(formData.company_id)) newErrors.company_id = 'Company is required';
    if (formData.site_ids.length === 0) newErrors.site_ids = 'At least one site must be selected';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      updateData.company_id = parseInt(updateData.company_id);
      updateData.site_ids = updateData.site_ids.map(id => parseInt(id));

      await onSubmit(spoc.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error updating SPOC:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setErrors({});
      onClose();
    }
  };

  const companyOptions = [
    { value: '', label: '-- Select Company --' },
    ...companies.map(c => ({ value: c.id.toString(), label: c.company_name }))
  ];

  const siteOptions = sites.map(s => ({ 
    value: s.id.toString(), 
    label: s.name || s.site_name,
    subtitle: s.location || (companies.find(c => c.id === s.company_id)?.company_name)
  }));

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Updating...' : 'Update SPOC'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Edit SPOC - ${spoc?.name}`} size="medium" footer={footer}>
      <form onSubmit={handleSubmit}>
        <Input label="Full Name" name="name" placeholder="e.g., Jane Doe" value={formData.name} onChange={handleChange} error={errors.name} required />
        <Input label="Designation" name="designation" placeholder="e.g., Facility Manager" value={formData.designation} onChange={handleChange} error={errors.designation} />
        <div style={gridStyle}>
          <Input label="Email" name="email" type="email" placeholder="jane@clientcorp.com" value={formData.email} onChange={handleChange} error={errors.email} required />
          <Input label="Phone Number" name="contact" placeholder="9876543210" value={formData.contact} onChange={handleChange} error={errors.contact} required />
        </div>

        {/* <Input
          label="Team Lead"
          name="team_lead_name"
          placeholder="e.g., Sarah Johnson"
          value={formData.team_lead_name}
          onChange={handleChange}
          error={errors.team_lead_name}
          required
        /> */}

        <div style={gridStyle}>
          <Select
            label="Company"
            name="company_id"
            value={formData.company_id}
            onChange={handleChange}
            options={companyOptions}
            error={errors.company_id}
            disabled={loadingCompanies}
            required
          />
        </div>

        <SearchableMultiSelect
          label="Assigned Sites"
          options={siteOptions}
          selectedValues={formData.site_ids}
          onChange={handleSitesChange}
          error={errors.site_ids}
          loading={loadingSites}
          placeholder="Search sites by name or location..."
          emptyMessage={!formData.company_id ? "Please select a company first to view available sites." : "No sites found for this company."}
          required
        />

        <Input label="New Password" name="password" type="password" canPeekPassword placeholder="Leave blank to keep unchanged" value={formData.password} onChange={handleChange} error={errors.password} />

        <label style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: formData.is_locked_out ? '#fee2e2' : 'var(--surface-light)', border: formData.is_locked_out ? '1px solid #ef4444' : 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '16px'
        }}>
          <input type="checkbox" name="is_locked_out" checked={formData.is_locked_out} onChange={handleChange} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
          <span style={{ fontSize: '14px', color: formData.is_locked_out ? '#ef4444' : 'var(--text-primary)', fontWeight: '500' }}>Account Locked (Uncheck to reinstate access)</span>
        </label>

        {spoc?.active_tickets > 0 && (
          <div style={{
            padding: '12px 16px', background: '#f59e0b20', border: '1px solid #f59e0b', borderRadius: '8px', fontSize: '13px', color: '#f59e0b', marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '4px'
          }}>
             <strong>Notice:</strong> This SPOC currently has <strong>{spoc.active_tickets}</strong> active ticket(s).
          </div>
        )}
      </form>
    </Modal>
  );
};

export default EditSpocModal;